import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Divider } from '@mui/material';
import { t } from '../../../utils/textUtils';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import FolderIcon from '@mui/icons-material/Folder';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import BrushIcon from '@mui/icons-material/Brush';
import HistoryIcon from '@mui/icons-material/History';
import BugReportIcon from '@mui/icons-material/BugReport';
import InfoIcon from '@mui/icons-material/Info';
import BusinessIcon from '@mui/icons-material/Business';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ConstructionIcon from '@mui/icons-material/Construction';
import CampaignIcon from '@mui/icons-material/Campaign';

// Import admin panel components
import UserManagement from './UserManagement';
import RoleManagement from './RoleManagement';
import ProjectManagement from './ProjectManagement';
import IntegrationSettings from './IntegrationSettings';
import BrandingSettings from './BrandingSettings';
import AuditLog from './AuditLog';
import TestingSimulation from './TestingSimulation';
import SystemInfo from './SystemInfo';
import ClientManagement from './ClientManagement';
import ProductionCoordinatorManagement from './ProductionCoordinatorManagement';
import SubcontractorManagement from './SubcontractorManagement';
import BlastNotification from './BlastNotification';

const AdminPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/settings/admin/users')) return 0;
    if (path.includes('/settings/admin/roles')) return 1;
    if (path.includes('/settings/admin/projects')) return 2;
    if (path.includes('/settings/admin/clients')) return 3;
    if (path.includes('/settings/admin/production-coordinators')) return 4;
    if (path.includes('/settings/admin/installation-teams')) return 5;
    if (path.includes('/settings/admin/integration')) return 6;
    if (path.includes('/settings/admin/branding')) return 7;
    if (path.includes('/settings/admin/audit')) return 8;
    if (path.includes('/settings/admin/testing')) return 9;
    if (path.includes('/settings/admin/system')) return 10;
    if (path.includes('/settings/admin/blast-notif')) return 11;
    return 0; // Default to users
  };

  const [value, setValue] = useState(getActiveTab());

  const handleChange = (event, newValue) => {
    setValue(newValue);
    switch (newValue) {
      case 0:
        navigate('/settings/admin/users');
        break;
      case 1:
        navigate('/settings/admin/roles');
        break;
      case 2:
        navigate('/settings/admin/projects');
        break;
      case 3:
        navigate('/settings/admin/clients');
        break;
      case 4:
        navigate('/settings/admin/production-coordinators');
        break;
      case 5:
        navigate('/settings/admin/installation-teams');
        break;
      case 6:
        navigate('/settings/admin/integration');
        break;
      case 7:
        navigate('/settings/admin/branding');
        break;
      case 8:
        navigate('/settings/admin/audit');
        break;
      case 9:
        navigate('/settings/admin/testing');
        break;
      case 10:
        navigate('/settings/admin/system');
        break;
      case 11:
        navigate('/settings/admin/blast-notif');
        break;
      default:
        navigate('/settings/admin/users');
    }
  };

  return (
    <Box sx={{ width: '100%', px: 0 }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {t('ADMIN PANEL')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('KELOLA PENGGUNA, PERAN, PROYEK, DAN PENGATURAN SISTEM')}
        </Typography>
      </Box>
      
      <Box sx={{ mb: 2, bgcolor: 'background.paper', borderRadius: '4px 4px 0 0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="admin panel tabs"
          sx={{ minHeight: '48px' }}
        >
          <Tab icon={<PersonIcon />} label={t('MANAJEMEN PENGGUNA')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<SecurityIcon />} label={t('PERAN & AKSES')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<FolderIcon />} label={t('MANAJEMEN PROYEK')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<BusinessIcon />} label={t('PELANGGAN')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<EngineeringIcon />} label={t('KOORDINATOR PRODUKSI')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<ConstructionIcon />} label={t('SUBKONTRAKTOR')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<IntegrationInstructionsIcon />} label={t('INTEGRASI')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<BrushIcon />} label={t('BRANDING')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<HistoryIcon />} label={t('LOG AUDIT')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<BugReportIcon />} label={t('TESTING & SIMULASI')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<InfoIcon />} label={t('INFO SISTEM')} iconPosition="start" sx={{ minHeight: '48px' }} />
          <Tab icon={<CampaignIcon />} label={t('KIRIM BLAST NOTIF')} iconPosition="start" sx={{ minHeight: '48px' }} />
        </Tabs>
        <Divider />
      </Box>
      
      <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: '0 0 4px 4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {/* Routes for admin panel components */}
        <Routes>
          <Route path="users" element={<UserManagement />} />
          <Route path="roles" element={<RoleManagement />} />
          <Route path="projects" element={<ProjectManagement />} />
          <Route path="clients" element={<ClientManagement />} />
          <Route path="production-coordinators" element={<ProductionCoordinatorManagement />} />
          <Route path="installation-teams" element={<SubcontractorManagement />} />
          <Route path="integration" element={<IntegrationSettings />} />
          <Route path="branding" element={<BrandingSettings />} />
          <Route path="audit" element={<AuditLog />} />
          <Route path="testing" element={<TestingSimulation />} />
          <Route path="system" element={<SystemInfo />} />
          <Route path="blast-notif" element={<BlastNotification />} />
          <Route path="*" element={<UserManagement />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default AdminPanel;
